import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faList } from '@fortawesome/pro-regular-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_MY_CLUB_SLUG_WATCHLISTS } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { glossary } from '@sorare/core/src/lib/glossary';

import { WatchlistItem } from '@sorare/marketplace/src/components/watchlists/WatchlistItem';

import type { WatchlistsBlock_user } from './__generated__/index.graphql';

const Root = styled(Vertical)`
  padding: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--double-unit);
  background: var(--c-black);
`;

const ItemsContainer = styled(Vertical).attrs({ gap: 1.5 })`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  column-gap: var(--double-unit);
  row-gap: var(--triple-unit);
`;

type Props = {
  user: WatchlistsBlock_user;
};

export const WatchlistsBlock = ({ user }: Props) => {
  const tabletAndAbove = useIsTabletAndAbove();
  const { generateSportContextPath } = useSportContext();

  const { publicWatchlists } = user;

  if (!publicWatchlists?.nodes.length) {
    return null;
  }
  const watchlists = tabletAndAbove
    ? publicWatchlists.nodes.slice(0, 2)
    : publicWatchlists.nodes;

  return (
    <Root>
      <Vertical gap={3}>
        <Horizontal>
          <FontAwesomeIcon icon={faList} />
          <LabelL bold>
            <FormattedMessage
              id="UserWatchlists.title"
              defaultMessage="{count, plural, one {# public watchlist} other {# public watchlists}}"
              values={{ count: publicWatchlists.nodes.length }}
            />
          </LabelL>
        </Horizontal>
        <ItemsContainer gap={1.5}>
          {watchlists.map(w => {
            return <WatchlistItem key={w.slug} watchlist={w} hideOwner />;
          })}
        </ItemsContainer>
        <Button
          to={generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG_WATCHLISTS, {
            params: { slug: user.slug },
          })}
          size="medium"
          color="quaternary"
        >
          <FormattedMessage {...glossary.viewAll} />
        </Button>
      </Vertical>
    </Root>
  );
};

WatchlistsBlock.fragments = {
  user: gql`
    fragment WatchlistsBlock_user on User {
      slug
      id
      publicWatchlists(sport: $sport, first: 4) {
        nodes {
          slug
          ...WatchlistItem_watchlist
        }
      }
    }
    ${WatchlistItem.fragments.watchlist}
  ` as TypedDocumentNode<WatchlistsBlock_user>,
};
